import { Arrays } from "src/modules/utils/shared/arrays";
import { Objects } from "src/modules/utils/shared/objects";
import { OrdinaryObject } from "src/modules/utils/shared/ordinary-object.model";
import { Strings } from "src/modules/utils/shared/strings";
import { Utils } from "src/modules/utils/shared/utils";
import { TableCellType } from "../netgen/TableCellType";
import { TableColumn } from "../netgen/TableColumn";
import { TableCell } from "./table-cell.model";
import { TableRow } from "./table-row.model";
import { TableSortColumn } from "./table-sort-column.model";

export class TableData<T = any> {

    static fromList(data: OrdinaryObject[], cols?: string[], unroll?: boolean, addIndexColumn?: boolean): TableData {
        if (cols == null) {
            cols = Arrays.sort(Arrays.getUnique(Arrays.explode(data, item => Objects.getPropertyNames(item))));
        }
        return new TableData(
            Arrays.withoutNull([
                addIndexColumn ? new TableColumn("__index", "Index", TableCellType.int, 5) : null,
                ...cols.map(col => new TableColumn(col, col, TableCellType.string, -1))
            ]), Arrays.mapWithIndex(data, (item, index) => {
            let value = item;
            if (unroll) {
                value = { };
                for (let col of cols) {
                    value[col] = Objects.getPropertyNested(item, col);
                }
            }
            value.__index = index;
            return new TableRow("", item, value);
        }));
    }

    static update(data: TableData, columns: TableColumn[], rows: TableRow[], sort: (string | TableSortColumn)[] = null, generator: () => Promise<TableRow> = null): TableData {
        if (data != null) {
            data.rows = rows;
            data.useRowIds = rows?.length > 0 && !Utils.isNoe(rows[0].id);
            return data;
        }
        else {
            return new TableData(columns, rows, sort?.map(c => Strings.is(c) ? new TableSortColumn(c as string) : Objects.castOrThrow(c, TableSortColumn)), { generator });
        }
    }

    columns: TableColumn[];
    sort: TableSortColumn[];
    useRowIds = false;
    pinnedBottomRows: TableRow[];
    generator: () => Promise<TableRow> = null;
    
    constructor(columns: TableColumn[], public rows: TableRow<T>[], sort: (string | TableSortColumn)[] = null, props?: Partial<TableData>) {
        this.columns = columns != null ? Arrays.withoutNull(columns) : null;
        this.sort = sort?.map(c => Strings.is(c) ? new TableSortColumn(c as string) : Objects.castOrThrow(c, TableSortColumn));
        this.useRowIds = rows?.length > 0 && !Utils.isNoe(rows[0].id);
        if (props != null) {
            Object.assign(this, props);
        } 
    }

    colorColumn(columnId: string): void {
        let colors = Utils.assignDistinctColors(this.rows.map(row => Objects.toString(row.values[columnId])));
        for (let row of this.rows) {
            let cell = row.values[columnId];
            if (cell instanceof TableCell) {
                cell.style = { "background-color": Utils.toColorHexString(colors[Objects.toString(row.values[columnId])]) }; 
            }
            else {
                row.values[columnId] = new TableCell(cell, { style: { "background-color": Utils.toColorHexString(colors[Objects.toString(row.values[columnId])]) }});
            }
        }
    }

    toCsvCell(cell: string): string {
        cell = Strings.replaceAll(Strings.replaceAll(cell, "\r", "\\r"), "\n", "\\n");
        if (cell.includes(";") || cell.includes("\"")) {
            return "\"" + Strings.replaceAll(cell, "\"", "\"\"") + "\"";
        }
        else {
            return cell;
        }
    }

    toCsv(rows: TableRow[] = null): string {
        let cols = this.columns.filter(col => col.cellType != TableCellType.button);
        let result = Arrays.itemsToString(cols, ";", col => this.toCsvCell(col.getFullCaption()));
        for (let row of rows ?? this.rows) {
            if (row.virtual) {
                continue;
            }
            try {
                for (let col of cols) {
                    if (!Objects.hasProperty(row.values, col.id)) {
                        throw new Error("Das Objekt enthält nicht das Feld " + col.id);
                    }
                }
                result += "\r\n" + Arrays.itemsToString(cols, ";", col => this.toCsvCell(col.toCsvString(row.values[col.id])));
            }
            catch (error: any) {
                throw new Error("Konnte Zeile nicht kopieren. " + Objects.toJson(row.values, true) + ". Columns: " + Arrays.itemsToString(cols.map(col => col.id)) + " Fehlermeldung: " + error);
            }
        }
        return result;
    }

    toAsciiTable(rows: TableRow[] = null): string {
        let cols = this.columns.filter(col => col.cellType != TableCellType.button, 1);
    
        let colWidths = cols.map(col => {
            let maxWidth = col.getFullCaption().length;
            for (let row of rows ?? this.rows) {
                let cellValue = col.toCsvString(row.values[col.id] ?? "");
                maxWidth = Math.max(maxWidth, cellValue.length);
            }
            return maxWidth;
        });
    
        const formatRow = (row: string[]) => '| ' + row.map((cell, i) => cell.padEnd(colWidths[i], ' ')).join(' | ') + ' |';
        const drawLine = () => '+-' + colWidths.map(width => '-'.repeat(width)).join('-+-') + '-+';
    
        let result = drawLine() + '\n' + formatRow(cols.map(col => col.getFullCaption())) + '\n' + drawLine() + '\n';
    
        for (let row of rows ?? this.rows) {
            if (row.virtual) {
                continue;
            }
            try {
                result += formatRow(cols.map(col => col.toCsvString(row.values[col.id] ?? ''))) + '\n';
            } 
            catch (error: any) {
                throw new Error("Konnte Zeile nicht kopieren. " + Objects.toJson(row.values, true) + ". Columns: " + Arrays.itemsToString(cols.map(col => col.id)) + " Fehlermeldung: " + error);
            }
        }    
        result += drawLine();
    
        return result;
    }

    toId(): string {
        return Arrays.itemsToString(this.columns, ", ", col => col.id);
    }

}